import { useCallback, useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { googlePlacesFields } from '@/domains/Business/constants'
import { GooglePlacesParsedAddress } from '@/lib/address'
import { AddressAutocompleteField, GoBackButton } from '@/shared/components'
import {
  Button,
  Form,
  FormField,
  MotionSpan,
  SlideInScreen,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { onboardingAddressFieldsOrder } from '../../constants'
import {
  BusinessAddressStep as BusinessAddressStepType,
  OnboardingStep,
  OnboardingStepConfig,
} from '../../types'
import { AddressFields } from '../AddressFields'

import { businessAddressStepSchema, BusinessAddressStepSchema } from './schema'

const REGISTERED_ADDRESS_FORM_ID = 'registered-address-form-id'

type Props = {
  config?: OnboardingStepConfig
  steps: OnboardingStep[]
  stepDetails?: BusinessAddressStepType['stepDetails']
  registeredAddress?: BusinessAddressStepSchema
  onContinue: (data: BusinessAddressStepSchema) => void
  onBack: () => void
}

export const RegisteredAddressStep = ({
  config,
  registeredAddress,
  stepDetails,
  onContinue,
  onBack,
}: Props) => {
  const [inputView, setInputView] = useState<'manual' | 'auto'>('auto')

  const intl = useIntl()

  const form = useForm<BusinessAddressStepSchema>({
    mode: 'onChange',
    resolver: zodResolver(businessAddressStepSchema),
    values: {
      GOOGLE_PLACES_ADDRESS: '',
      ADDRESS_STREET: registeredAddress?.ADDRESS_STREET
        ? registeredAddress.ADDRESS_STREET
        : stepDetails?.addressStreet
          ? stepDetails.addressStreet
          : '',
      ADDRESS_STREET_NUMBER: registeredAddress?.ADDRESS_STREET_NUMBER
        ? registeredAddress.ADDRESS_STREET_NUMBER
        : stepDetails?.addressStreetNumber
          ? stepDetails.addressStreetNumber
          : '',
      ADDRESS_DISTRICT: registeredAddress?.ADDRESS_DISTRICT
        ? registeredAddress.ADDRESS_DISTRICT
        : stepDetails?.addressDistrict
          ? stepDetails.addressDistrict
          : '',
      ADDRESS_CITY: registeredAddress?.ADDRESS_CITY
        ? registeredAddress.ADDRESS_CITY
        : stepDetails?.addressCity
          ? stepDetails.addressCity
          : '',
      ADDRESS_STATE: registeredAddress?.ADDRESS_STATE
        ? registeredAddress.ADDRESS_STATE
        : stepDetails?.addressState
          ? stepDetails.addressState
          : '',
      ADDRESS_POST_CODE: registeredAddress?.ADDRESS_POST_CODE
        ? registeredAddress.ADDRESS_POST_CODE
        : stepDetails?.addressPostCode
          ? stepDetails.addressPostCode
          : '',
      ADDRESS_COUNTRY: registeredAddress?.ADDRESS_COUNTRY
        ? registeredAddress.ADDRESS_COUNTRY
        : stepDetails?.addressCountry
          ? stepDetails.addressCountry
          : '',
    },
  })

  const getFilesByField = useCallback(
    (fieldName: string) => {
      return (stepDetails?.documents ?? []).filter(
        (doc) => doc.documentType === fieldName,
      )
    },
    [stepDetails?.documents],
  )

  const fields = form.watch()

  useEffect(() => {
    if (
      registeredAddress?.ADDRESS_STREET ||
      stepDetails?.physicalAddressStreet
    ) {
      setInputView('manual')
    }
  }, [registeredAddress?.ADDRESS_STREET, stepDetails?.physicalAddressStreet])

  const hasAllRequiredFields = useMemo(() => {
    if (!config?.fields) {
      return false
    }

    const filteredRequiredFields = config.fields.filter((field) =>
      onboardingAddressFieldsOrder.includes(
        field.name as (typeof onboardingAddressFieldsOrder)[number],
      ),
    )

    const requiredFields = filteredRequiredFields.filter(
      (field) => field.required,
    )

    return requiredFields.every(
      (field) => fields[field.name as keyof typeof fields],
    )
  }, [config?.fields, fields])

  return (
    <>
      <GoBackButton className="hidden md:left-80 md:flex" onClick={onBack} />

      <SlideInScreen>
        <Typography variant="h3" text="center">
          <FormattedMessage
            defaultMessage="Business registered address"
            id="onboarding.registeredAddress.title"
          />
        </Typography>

        <div className="p-2" />

        <MotionSpan key={inputView}>
          <Typography text="center">
            {inputView === 'manual' ? (
              <FormattedMessage
                defaultMessage="Review the address carefully and continue"
                id="onboarding.registeredAddress.review.subtitle"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Search for the address to get it automatically filled in"
                id="onboarding.sameBusinessAddress.subtitle"
              />
            )}
          </Typography>
        </MotionSpan>

        <div className="p-6" />

        <Form {...form}>
          <form
            id={REGISTERED_ADDRESS_FORM_ID}
            onSubmit={form.handleSubmit(onContinue)}
          >
            {inputView === 'auto' ? (
              <FormField
                control={form.control}
                name="GOOGLE_PLACES_ADDRESS"
                render={({ field }) => (
                  <AddressAutocompleteField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    disabled={field.disabled}
                    placeholder={intl.formatMessage({
                      id: 'label.businessAddress',
                      defaultMessage: 'Business address',
                    })}
                    onManualClick={() => setInputView('manual')}
                    onGoogleAddressSelected={(address) => {
                      onboardingAddressFieldsOrder.forEach((field, index) => {
                        form.setValue(
                          field,
                          address[
                            googlePlacesFields[
                              index
                            ] as keyof GooglePlacesParsedAddress
                          ],
                        )
                      })

                      setInputView('manual')
                    }}
                  />
                )}
              />
            ) : (
              <AddressFields
                type="registered"
                config={config}
                form={form}
                isPending={false}
                stepName={config?.name ?? ''}
                getFilesByField={getFilesByField}
              />
            )}
          </form>
        </Form>

        <StickyContainer>
          <Button
            width="full"
            disabled={!hasAllRequiredFields}
            form={REGISTERED_ADDRESS_FORM_ID}
            onClick={form.handleSubmit(onContinue)}
            type="submit"
          >
            <FormattedMessage
              defaultMessage="Save & Continue"
              id="action.saveAndContinue"
            />
          </Button>

          {inputView === 'manual' && (
            <Button
              type="button"
              onClick={() => {
                setInputView('auto')
                form.setValue('GOOGLE_PLACES_ADDRESS', '')
              }}
              width="full"
              className="text-center font-semibold"
              variant="ghost"
            >
              <FormattedMessage
                defaultMessage="Search for my address"
                id="onboarding.businessAddress.searchForAddress"
              />
            </Button>
          )}
        </StickyContainer>
      </SlideInScreen>
    </>
  )
}
